import {
  Action,
  BannerVariant,
  CargoTypes,
  CategoryFieldType,
  CatUploadStatus,
  ClusterTypes,
  CommonModals,
  ComplexOrderStatus,
  DeliveryService,
  DeliveryStatus,
  DeliveryType,
  EntityType,
  EquipmentTypes,
  FieldType,
  Group,
  LegalEntityType,
  NotificationTypes,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  Status,
  SystemType,
  TaskStatus,
  TransportLoadCapacities,
  TransportSideTypes,
  TransportTypes,
  Unit,
  ValueType,
} from './enums'

export interface ValidationsInterface {
  isEmpty?: boolean
  minLength?: number
  maxLength?: number
  validFormat?: RegExp
}

export interface ValidationResultInterface {
  [key: string]: string | boolean
  name: string
  isDirty: boolean
  isEmpty: boolean
  notValidFormat: boolean
  tooShort: boolean
  tooLong: boolean
  isEmptyError: boolean
  notValid: boolean
  errorMessage: string
}

export interface ValidationRulesInterface {
  fieldName: string
  minLength?: number
  maxLength?: number
  format?: RegExp | ''
  canBeEmpty?: boolean
  isEmptyMessage: string
  toShortMessage: string
  tooLongMessage: string
  formatMessage: string
}

export interface TransportItemInterface {
  [key: string]:
    | string
    | number
    | boolean
    | Date
    | null
    | (string | undefined)[]
    | undefined
    | Regularity
    | Regularity[]
    | []
    | CityInterface
    | AddressInterface
    | PointItemInterface[]
    | Group
    | ViewItem[]
    | CargoUnit[]
    | number[]
  id?: number
  name: string
  description: string
  startTime: Date
  endTime: Date
  regularity?: Regularity | Regularity[] | []
  files?: string[] | []
  points?: PointItemInterface[] | []
  route?: string
  cost?: number | string | undefined
  userPhone?: string
  intercity?: boolean
  status?: Status
  userId?: number
  yandex_route_url?: string
  google_route_url?: string
  double_gis_route_url?: string
  group?: Group
  archived_participant?: boolean
  archived_user?: boolean
  deleted_user?: boolean
  deleted_participant?: boolean
  executorId?: number
  routeId?: number

  //new instances
  views: ViewItem[]
  contacts: ViewItem[]
  selected: ViewItem[]
  cargo_units: CargoUnit[]
  transport_types: number[]
  equipment_types: number[]
  cargo_types: number[]
  transport_side_types: number[]
  transport_load_capacities: number[]

  start_important: boolean
  end_important: boolean

  createdAt?: Date
}

export interface SaleListResponse {
  data: SaleItemInterface[]
  total: number
}
export interface StoreListResponse {
  data: StoreInterface[]
  total: number
}
export interface ClusterListResponse {
  data: ClusterInterface[]
  total: number
}
export interface PromoListResponse {
  data: PromoInterface[]
  total: number
}
export interface SlotListResponse {
  data: SlotItemInterface[]
  total: number
}

export interface TransportListResponse {
  data: TransportItemInterface[]
  total: number
}
export interface CargoListResponse {
  data: CargoItemInterface[]
  total: number
}

export interface CargoItemInterface {
  [key: string]:
    | string
    | number
    | boolean
    | Date
    | null
    | (string | undefined)[]
    | undefined
    | Regularity
    | Regularity[]
    | []
    | CityInterface
    | AddressInterface
    | PointItemInterface[]
    | Group
    | ViewItem[]
    | CargoUnit[]
    | number[]

  id?: number
  name: string
  description: string
  startTime: Date
  endTime: Date
  regularity?: Regularity | Regularity[] | []
  files?: string[] | []
  points?: PointItemInterface[] | []
  route?: string
  cost?: number | string | undefined
  userPhone?: string
  intercity?: boolean
  status?: Status
  userId?: number
  yandex_route_url?: string
  google_route_url?: string
  double_gis_route_url?: string
  group?: Group
  archived_participant?: boolean
  archived_user?: boolean
  deleted_user?: boolean
  deleted_participant?: boolean
  executorId?: number
  routeId?: number

  //new instances
  views: ViewItem[]
  contacts: ViewItem[]
  selected: ViewItem[]
  cargo_units: CargoUnit[]
  transport_types: number[]
  equipment_types: number[]
  cargo_types: number[]
  transport_side_types: number[]
  transport_load_capacities: number[]

  start_important: boolean
  end_important: boolean

  createdAt?: Date
}

export interface GetCodePayload {
  [key: string]: string | undefined
  phone_number: string
}

export interface CheckCodePayload {
  [key: string]: string | undefined
  code?: string
  request_id?: string
}

export enum Regularity {
  'NO',
  'DAY',
  'WEEK',
  'MONTH',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'SATURDAY',
  'SUNDAY',
}

export enum LoginStage {
  'getCode',
  'checkCode',
  'done',
  'botLoginProcess',
  'botLoginSuccess',
  'botLoginError',
  'botLink',
}

export interface GetCodeResponse {
  ok: boolean
  result: {
    request_id: string
    phone_number: string
    request_cost: 0
    remaining_balance: 0
    delivery_status: {
      status: string
      updated_at: number
    }
    payload: string
  }
}

export interface CheckCodeResponse {
  ok: boolean
  result: {
    request_id: string
    phone_number: string
    request_cost: number
    delivery_status: { status: string; updated_at: number }
    verification_status: { status: string; updated_at: number; code_entered: string }
    payload: string
  }
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  tokenExpiry: number
  user: UserInterface
}

export interface AccessPayload {
  id: number
  userId: number
  iat: number
  exp: number
  contacts: number
  phone_number?: string
  uuid?: string
  email?: string
  clusterId?: number
  user_clusters?: number[]
}

export interface AuthState {
  token: string
}

export interface WsState {
  connected: boolean
  connectedList: Client[] | []
}

export interface NotificationInterface {
  id: string
  type: NotificationTypes
  text: string
}
export interface NotificationList {
  data: NotificationInterface[]
}

export interface UserInterface {
  [key: string]: number | undefined | string | number[]
  id?: number
  uuid?: string
  phone_number?: string
  contacts?: number
  user_clusters?: number[]
}

export interface CheckCodeResponseAndToken {
  user: UserInterface
  accessToken: string
  refreshToken: string
}
export interface BotAuthResponseAndToken {
  accessToken?: string
  refreshToken?: string
  user?: {
    id: number
    phone_number: string
  }
  dateNotValid?: boolean
  registerAction?: boolean
}

export interface CityInterface {
  id?: number
  createdAt?: string
  updatedAt?: string
  value: string
  latitude: number | string | undefined
  longitude: number | string | undefined
  isDirty?: boolean
  notValid?: boolean
}

export interface AddressInterface {
  id?: number
  createdAt?: string
  updatedAt?: string
  value: string
  latitude: number | string | undefined
  longitude: number | string | undefined
  isDirty?: boolean
  notValid?: boolean
  cityId?: number
}

export interface SlotItemInterface {
  [key: string]:
    | string
    | number
    | boolean
    | Date
    | null
    | (string | undefined)[]
    | undefined
    | Regularity
    | Regularity[]
    | []
    | CityInterface
    | AddressInterface
    | PointItemInterface[]
    | Group
    | ViewItem[]
    | CargoUnit[]
    | number[]
  id?: number
  name: string
  description: string
  startTime: Date
  endTime: Date
  regularity?: Regularity | Regularity[] | []
  files?: string[] | []
  points?: PointItemInterface[] | []
  route?: string
  cost?: number | string | undefined
  userPhone?: string
  intercity?: boolean
  status?: Status
  userId?: number
  yandex_route_url?: string
  google_route_url?: string
  double_gis_route_url?: string
  group?: Group
  archived_participant?: boolean
  archived_user?: boolean
  deleted_user?: boolean
  deleted_participant?: boolean
  createdAt?: Date
  updatedAt?: Date
  executorId?: number
  routeId?: number

  //new instances
  views: ViewItem[]
  contacts: ViewItem[]
  selected: ViewItem[]
  cargo_units: CargoUnit[]
  transport_types: number[]
  equipment_types: number[]
  cargo_types: number[]
  transport_side_types: number[]
  transport_load_capacities: number[]

  start_important: boolean
  end_important: boolean
}

export interface TypesObject {
  transport_types: number[]
  equipment_types: number[]
  cargo_types: number[]
  transport_side_types: number[]
  transport_load_capacities: number[]
}

export interface SlotPayload {
  id?: number
  name: string
  description: string
  startTime: Date
  endTime: Date
  regularity?: Regularity | Regularity[] | []
  points: PointItemInterface[]
  route?: string
  cost?: number | string | undefined
  cargo_units: CargoUnit[]
  cargo_types: number[]
  transport_types: number[]
  equipment_types: number[]
  transport_side_types: number[]
  transport_load_capacities: number[]
  file: FileList
}

export interface TransportPayload {
  id?: number
  name: string
  description: string
  startTime: Date
  endTime: Date
  regularity?: Regularity | Regularity[] | []
  points: PointItemInterface[]
  route?: string
  cost?: number | string | undefined
  cargo_units: CargoUnit[]
  cargo_types: number[]
  transport_types: number[]
  equipment_types: number[]
  transport_side_types: number[]
  transport_load_capacities: number[]
  file: FileList
}

export interface CargoPayload {
  id?: number
  name: string
  description: string
  startTime: Date
  endTime: Date
  regularity?: Regularity | Regularity[] | []
  points: PointItemInterface[]
  route?: string
  cost?: number | string | undefined
  cargo_units: CargoUnit[]
  cargo_types: number[]
  transport_types: number[]
  equipment_types: number[]
  transport_side_types: number[]
  transport_load_capacities: number[]
  file: FileList
}

export interface DataInterface {
  slotList: SlotListResponse
  cargoList: CargoListResponse
  transportList: TransportListResponse
}

export interface PairInterface {
  file: File
  url: string
  type?: 'image' | 'video' | 'document' | 'poster'
}

export interface UploadInterface {
  group: Group
  id: number
  action: Action
  files: FileList
}

export interface DownloadInterface {
  path: string
}

export interface GMapState {
  sliderVisible: boolean
}

export interface Location {
  lat: number
  lng: number
}

export interface PointItemInterface {
  [key: string]: any
  id?: string | number
  sequence: number
  city: CityInterface
  address: AddressInterface
  status?: Status
}

export interface GMapDataInterface {
  value: string
  latitude: number | undefined
  longitude: number | undefined
  notValid: boolean
  isDirty: boolean
}

export interface SettingsInterface {
  darkTheme: boolean
  soundEnabled: boolean
}

export interface CommonModalState {
  [CommonModals.LOGIN]: boolean
  [CommonModals.PAYMENT]: boolean
  [CommonModals.STATUS_MENU]: boolean
  [CommonModals.CHAT_MENU]: boolean
  [CommonModals.FILTER]: boolean
  [CommonModals.SORT]: boolean
  [CommonModals.LOADER]: boolean
}

export interface CommonModalPayload {
  modal: CommonModals
  state: boolean
}

export interface UpdateStatusPayload {
  route: string
  id: number
  status: Status
}

export interface ListParams {
  limit?: number
  offset?: number
  lte?: Date
  gte?: Date
  search?: string
  sort?: string
  order?: 'DESC' | 'ASC'
  cityId?: string
  searchFields?: string[]
  status?: string | number
  userId?: string
  selected?: boolean
  transport_types?: number[] | []
  equipment_types?: number[] | []
  cargo_types?: number[] | []
  transport_side_types?: number[] | []
  transport_load_capacities?: number[] | []
  types?: TypesObject
  all?: boolean
}

export interface FilterEmitPayload {
  filters: ListParams
  token: string
}

export interface SlotListResponse {
  data: SlotItemInterface[]
  total: number
}

export interface TransportListResponse {
  data: TransportItemInterface[]
  total: number
}

export interface CargoListResponse {
  data: CargoItemInterface[]
  total: number
}

export interface GetContactParams {
  userId: number
}

export interface PaymentPayload {
  price: number
  quantity: number
  description: string
}
export interface ClosePaymentPayload {
  id: string
}
export interface PaymentResult {
  token: string
  id: string
}

export interface Range {
  from: Date | ''
  to: Date | ''
}

export interface OptionItemInterface {
  [key: string]: string | number | undefined | boolean
  id: string | undefined
  name: string
  label: string
  hidden?: boolean
  selected?: boolean
  value?: string
}

export interface SortOption {
  id: number
  name: string
  value: string
}

export interface SortParams {
  sort: string
  order: 'ASC' | 'DESC'
}

export interface ChatMessage {
  id?: number
  userId?: number
  uuid: string
  participantId?: number
  value: string
  group: Group
  orderId?: number
  createdAt: Date | string
  topicId?: number
  read?: boolean
  archived_participant?: boolean
  archived_user?: boolean
  deleted_user?: boolean
  deleted_participant?: boolean
}

export interface Topic {
  messages: ChatMessage[]
  uuid: string
  group: Group
  id?: number
  userId?: number
  participantId?: number
  archived_user?: boolean
  archived_participant?: boolean
  deleted_user?: boolean
  deleted_participant?: boolean
  slotId?: number
  cargoId?: number
  transportId?: number
  createdAt?: Date
  orderId?: number
  executor_id?: number
}

export interface ChatGroupActionPayload {
  uuid: string[]
}

export interface SetExecutorPayload {
  topicId: number
  orderId: number
  group: Group
  executorId?: number
}
export interface Instruction {
  id: number
  title: string
  description: string
  images: string[] | []
}

export interface BotLoginPayload {
  id: string
  username: string
  auth_date: string
  hash: string
  photo_url: string
  first_name: string
  last_name: string
}

export interface IdPayload {
  id?: number
  el_id: number
  uuid?: string
  createdAt?: Date
  updatedAt?: Date
}

export interface BlockUserPayload {
  user_id: number
  reason?: number
  description?: string
}

export interface DeleteParams {
  id: number
}

export interface TypeSelectorItem {
  id: TransportTypes | TransportLoadCapacities | TransportSideTypes | EquipmentTypes | CargoTypes
  name: string
  image: string
  referenceTransportType?: TransportTypes[]
  referenceLoadCapacity?: TransportLoadCapacities[]
  referenceSideType?: TransportSideTypes[]
}

export interface CargoUnit {
  [key: string]: string | number | undefined
  id?: number
  width: string | number
  height: string | number
  length: string | number
  count: string | number
  weight?: string | number
  uuid?: string | number
}

export interface ViewItem {
  id?: number
  el_id?: number
  user_id: number
  uuid?: string
  createdAt?: Date
  updatedAt?: Date
}

export enum CargoOptions {}

export interface Client {
  clientId: string
  userId: number | string
  connectedAt: number
  topicId?: number
}

export interface BlockedItemInterface {
  userId: number
  reason: string
  description: string
  blockedId: number
}

export interface CreateRoutePayload {
  group: Group
  orderId: number
  route: string
}

export interface RouteInterface {
  id?: number
  route: string
}

export interface SharePayload {
  text: string
  url: string
}

export interface StockListResponse {
  data: StockItemInterface[]
  total: number
}
export interface PriceListResponse {
  data: PriceItemInterface[]
  total: number
}
export interface CategoryListResponse {
  data: CategoryInterface[]
  total: number
}

export interface ClusterPayload {
  [key: string]: any
  id?: number
  name: string
  description: string
  type: ClusterTypes | ''
  temporarilyDisabled?: boolean
  schedule?: DaySchedule[]
  withDelivery: boolean
  withBonuses: boolean
  createdAt?: Date
  updatedAt?: Date
  delivery: SettingsFormItem[]
  bonuses: SettingsFormItem[]
  isActive?: boolean
  forceStore?: boolean
  agreement?: string
  domain?: string

  complexSales: boolean
  regularSales: boolean

  // ========== ЮРИДИЧЕСКИЕ ДАННЫЕ ==========
  legalName?: string
  legalInn?: string
  legalAddress?: string
  legalEntityType?: LegalEntityType | string

  payment?: ClusterPaymentSettings

  showRating?: boolean
  manageStock?: boolean
  showOutOfStock?: boolean

  onePrice?: boolean
  cdek?: CdekSettings

  cutlery?: boolean

  autoCreateDeliveryTasks?: boolean
}

export interface ClusterPaymentSettings {
  cashOnDelivery?: boolean
  cardOnDelivery?: boolean
  onlineOnDelivery?: boolean
  billOnDelivery?: boolean
  cashOnPickup?: boolean
  cardOnPickup?: boolean
  onlineOnPickup?: boolean
  billOnPickup?: boolean
}

export interface CategoryInterface {
  [key: string]: any
  id?: number | string
  name: string
  description: string
  attributes: CategoryAttribute[]
  fields: CategoryFieldTemplateInterface[]
  files: string[]
  systemType?: SystemType
  isComplex?: boolean
  sortOrder: number | string
  isActive?: boolean
  children?: number[] | { childId: number }[] | CategoryInterface[]
}

export interface StoreDeliveryZone {
  id?: number
  storeId?: number
  clusterId?: number
  clusterDeliveryId: number
  polygon: Array<{ lat: number; lng: number }>
}

export interface StoreInterface {
  [key: string]: any
  id?: number
  name: string
  description: string
  address: AddressInterface
  city: CityInterface
  files: string[]
  file?: FileList
  createdAt?: Date | string
  updatedAt?: Date | string
  isActive: boolean
  temporarilyDisabled: boolean
  phone_number: string
  schedule?: DaySchedule[]
  sortOrder: number | string

  // ========== НАСТРОЙКИ ДОСТАВКИ ==========
  deliveryRadius: number | string
  withDelivery: boolean

  deliveryZones?: StoreDeliveryZone[]
}

export interface DaySchedule {
  sequence: number
  from: string
  to: string
  dayOff: boolean
}

export interface FullAddress {
  id?: number
  name: string
  description: string
  isGeneral: boolean
  deleted: boolean
  deletedAt?: string | null
  deletedById?: number | null
  userId: number
  clusterId: number
  city: CityInterface
  address: AddressInterface
  addressId?: number
  cityId?: number
  entrance?: string
  floor?: string
  apartment?: string
  intercom?: string
  createdAt?: Date | string
  updatedAt?: Date | string
}

export interface PromoInterface {
  id?: number
  createdAt?: Date
  updatedAt?: Date
  name: string
  description: string
  files: string[]
  isActive: boolean
  isVisible: boolean
  startDate: Date
  endDate: Date
  discount: string
  useItems: boolean
  itemsFor: number[]
  useCategories: boolean
  showText: boolean
  categoriesFor: number[]
  useStores: boolean
  storesFor: number[]
  useDiscountCode: boolean
  discountCode: string
  datesImportant: boolean
  file?: FileList
  clusterId?: number
  mailingSummary?: MailingSummaryInterface
  sortOrder: number | string
  forFirstPurchase: boolean // скидка только на первый чек/покупку

  forPickup?: boolean
  giftBySum?: boolean
  minSum?: number | string
  giftItemIds?: number[]
}

export interface MailingSummaryInterface {
  id?: number
  promoId: number
  clusterId: number
  status: 'not_started' | 'in_progress' | 'completed'
  sentCount: number
  openedCount: number
  totalCount: number
  startedAt?: Date
  completedAt?: Date
  createdAt?: Date
  updatedAt?: Date
}
export interface StockItemInterface {
  [key: string]: any
  id?: number
  saleItemId: number | ''
  storeId: number | ''
  count: number | ''
}
export interface PriceItemInterface {
  [key: string]: any
  id?: number
  saleItemId: number | ''
  storeId?: number | ''
  price: number | ''
}

export interface ItemStop {
  storeId: number
  isStopped: boolean
}
export interface SaleItemInterface {
  [key: string]: any
  id?: number
  name: string
  description: string
  files: string[]
  categoryId?: string
  barCode?: string
  isActive: boolean
  useStock: boolean
  attributes: ItemAttribute[]
  groupId?: string | null
  saleItemId?: string
  unit?: string
  isAddon?: boolean
  minQuantity: string
  quantityStep: string
  item_stops?: ItemStop[]
  isService: boolean
  items?: Array<{ childItemId: number; quantity: number; item?: SaleItemInterface }>
}

export interface CategoryAttribute {
  id: string | number
  name: string
  fieldType: FieldType
  valueType: ValueType
  options: string[]
  unit: Unit
  sortOrder: number
  isRequired: boolean
  isVisible: boolean

  //for later
  externalId?: string
}

export interface ItemAttribute {
  id?: string | number
  name: string
  fieldType: FieldType
  valueType: ValueType
  options: string[]
  unit: Unit
  sortOrder: number
  isRequired: boolean
  isVisible: boolean
  value: string | string[] | boolean
  categoryAttributeId?: string | number

  //for later
  externalId?: string
}

export interface OrderInterface {
  [key: string]: any
  id?: number
  status: OrderStatus
  deliveryType: DeliveryType
  fullAddressId?: number | null
  paymentMethod: PaymentMethod
  paymentStatus: PaymentStatus
  isPaid: boolean
  items?: OrderItem[]
  subtotal: number
  deliveryFee: number
  total: number
  createdAt?: string
  updatedAt?: string
  notes?: string | null
  orderNumber: string
  storeId?: number | null
  userId?: number
  clusterId?: number
  description?: string | null
  fullAddress?: FullAddress | null
  store?: StoreInterface | null
  uuid?: string
  user?: {
    phone_number: string
  }
  phone_number?: string
  baseAddress?: BaseAddress
  deliveryService?: DeliveryService
  delivery?: OrderDelivery[]
  tasks?: TaskInterface[]
}

export interface OrderDelivery {
  id?: number
  orderId?: number
  clusterId?: number
  service?: DeliveryService
  deliveryOrderId?: string
  deliveryOrderUuid?: string
  status?: DeliveryStatus
  tariffName?: string
  periodMin?: number
  periodMax?: number
  deliveryDateMin?: string
  deliveryDateMax?: string
  pickupPointCode?: string
  pickupPointUuid?: string
  pickupPointName?: string
  pickupPointAddress?: string
  pickupPointLatitude?: number
  pickupPointLongitude?: number
  pickupPointWorkTime?: string
  pickupPointPhone?: string
  createdAt?: string
  updatedAt?: string
}

export interface OrderItem {
  id?: number
  itemId: number
  name: string
  price: number
  quantity: number
  total: number
  createdAt?: string
  updatedAt?: string
  orderId?: number
  clusterId?: number
  added?: boolean
}

export interface BaseAddress {
  id?: number
  city: CityInterface
  address: AddressInterface
  userId?: number
  clusterId?: number
  createdAt?: Date | string
  updatedAt?: Date | string
  addressId?: number
  cityId?: number
}

// ФИНАЛЬНЫЕ ИНТЕРФЕЙСЫ

// Акция (только маркетинг)
// export interface Promo {
//   id?: number
//   name: string
//   description: string
//   files: string[]
//   isActive: boolean
//   createdAt?: Date | string
//   updatedAt?: Date | string
// }

export interface Promo {
  id: number
  title: string
  description: string
  images: string[]
  startDate: string
  endDate: string
  isActive: boolean
  discount?: number
  discountCode?: string
}

export interface SettingsFormItem {
  id: number
  level: number | string
  value: number | string
  radius: number | string
  fee?: number | string
  tag?: string
}

export interface SettingsFormItemPayload {
  level: number | string
  value: number | string
}

// Интерфейс для биллинга
export interface BillingInfo {
  monthlyPrice: number
  paidUntil: string | null
  blockingDelay: number
}

// Обновленный ClusterInterface
export interface ClusterInterface {
  [key: string]: any
  id?: number
  name: string
  description: string
  isActive: boolean
  token: string
  port: string
  type: ClusterTypes | ''
  withDelivery: boolean
  withBonuses: boolean
  complexSales: boolean
  regularSales: boolean
  createdAt?: Date
  updatedAt?: Date
  temporarilyDisabled: boolean
  phone_number: string
  schedule: DaySchedule[]
  delivery: SettingsFormItem[]
  bonuses: SettingsFormItem[]
  yookassa?: YookassaSettings
  payment?: ClusterPaymentSettings
  registeredUsersCount?: number | string
  forceStore?: boolean
  domain?: string
  billing?: BillingInfo
  tariff?: TariffRule
  showRating?: boolean
  manageStock?: boolean
  showOutOfStock?: boolean
  onePrice?: boolean
  cdek?: CdekSettings
  cutlery?: boolean
  autoCreateDeliveryTasks?: boolean
}

export interface CdekSettings {
  enabled: boolean
  insurance: boolean
  markup: number | string
  pickup: boolean
  courier: boolean
}

// 👇 Интерфейс настроек ЮKassa
export interface YookassaSettings {
  enabled: boolean
}

// Для документов
export interface StockDocumentInterface {
  [key: string]: any
  id?: number
  type: number // 0,1,2,3
  number: string
  date?: Date | string
  comment?: string
  storeId: number
  clusterId?: number
  createdBy?: number
  createdAt?: Date | string
  movements?: StockItemInterface[]
}

// Для остатков по магазинам
export interface StockStoreInterface {
  [key: string]: any
  id?: number
  quantity: number
  reserved: number
  itemId: number
  storeId: number
  clusterId: number
  createdAt?: Date | string
  updatedAt?: Date | string
  item?: SaleItemInterface
  store?: StoreInterface
}

export interface SyncOptions {
  createCategories: boolean
  createItems: boolean
  updatePrices: boolean
  updateImages: boolean
  createModifiers: boolean
  overwriteImages: boolean
}

export interface ManualSyncRequest {
  createCategories: boolean
  createItems: boolean
  updatePrices: boolean
  updateImages: boolean
  createModifiers: boolean
  clusterId: number
}

export interface ManualSyncResponse {
  success: boolean
  message: string
}

// Для фронта (то, что приходит с бека)

export interface Relation {
  id?: number
  itemId: number
  relationId: number
  createdAt?: string
  updatedAt?: string
}

// Для отправки на бек (Payload)
export interface RelationPayload {
  relationId: number
  // itemId не обязателен
}

export interface PriceDocumentInterface {
  [key: string]: any
  id: number
  type: number // PriceDocumentType
  number: string
  date: Date
  comment?: string
  clusterId: number
  createdBy: number
  createdAt: string
  updatedAt: string
  prices?: PriceItemInterface[]
}

export interface AuthResponse {
  accessToken?: string
  refreshToken?: string
  dateNotValid?: boolean
  registerAction?: boolean //
  message?: string // ← добавить
  type?: string // ← добавить
}

export interface EmailLoginFormData {
  [key: string]: any
  email: string
  code: string
}

export interface RegisterEmailPayload {
  email: string
  code: string
  clusterId?: string
}

export type BannerVariantConfigMap = {
  [K in BannerVariant]: BannerVariantConfig
}

export type BannerVariantConfig = {
  hasCarousel: boolean
  aspectRatio: {
    mobile: number | 'auto' // добавили 'auto'
    desktop: number | 'auto' // добавили 'auto'
  }
  contentAlign: 'center' | 'left' | 'right'
  overlay: boolean
}

export interface LeadFormData {
  phone: string
  consent?: boolean
}

// Интерфейсы
export interface StartMailingDto {
  promoId: number
}

export interface MailingStatusResponse {
  status: 'not_started' | 'in_progress' | 'completed'
  total: number
  sent: number
  opened: number
  failed: number
  pending: number
}

export interface PlaceItemInterface {
  placeId: number
  itemId: number
  item?: SaleItemInterface
}

export interface PlaceCategoryInterface {
  placeId: number
  categoryId: number
  category?: CategoryInterface
}

export interface PlaceInterface {
  [key: string]: any
  id?: number
  name: string
  description?: string
  placeNumber?: string
  isActive: boolean
  sortOrder: number | string

  slotDurationMinutes?: number | string
  slotIntervalMinutes?: number | string
  breakBetweenSlotsMinutes?: number | string

  minBookingDurationMinutes?: number | string
  maxBookingDurationMinutes?: number | string
  cancellationDeadlineMinutes?: number | string

  checkinTime?: string
  checkoutTime?: string

  itemIds?: number[]
  categoryIds?: number[]

  placeItems?: PlaceItemInterface[]
  placeCategories?: PlaceCategoryInterface[]

  uuid?: string
  clusterId?: number
  storeId?: number | string
  createdAt?: string
  updatedAt?: string
  deleted?: boolean
}

export interface MotivationInterface {
  id?: number
  name: string
  description?: string
  costPerOrder?: number
  revenuePercent?: number
  bonusForOrdersCount?: number
  ordersCountTarget?: number
  bonusForRevenue?: number
  revenueTarget?: number
  metadata?: Record<string, any>
  deleted?: boolean
  createdAt?: string
  updatedAt?: string
  rules?: MotivationRuleInterface[]
}

export interface MotivationRuleInterface {
  id?: number
  motivationId?: number
  categoryIds?: number[]
  itemIds?: number[]
  costPerOrder?: number
  revenuePercent?: number
  deleted?: boolean
  createdAt?: string
  updatedAt?: string
  categories?: Array<{
    ruleId: number
    categoryId: number
    category: {
      id: number
      name: string
    }
  }>
  items?: Array<{
    ruleId: number
    itemId: number
    item: {
      id: number
      name: string
    }
  }>
}

export interface ExecutorInterface {
  id?: number
  name: string
  phone_number: string
  position?: string
  isDriver: boolean
  canServe: boolean
  trackLocation: boolean
  isActive: boolean
  deleted: boolean
  publish: boolean
  publishMotivation: boolean
  uuid?: string
  inviteUuid?: string
  clusterId?: number
  motivationId: number | string
  userId?: number
  storeIds?: number[]
  motivation?: {
    id: number
    name: string
    description?: string
  }
  stores?: Array<{
    storeId: number
    executorId: number
    store: {
      id: number
      name: string
    }
  }>
  user?: {
    id: number
    name?: string
    phone_number?: string
    email?: string
  }
  createdAt?: string
  updatedAt?: string
  settings?:any
}

export interface StoreExecutorInterface {
  storeId: number
  executorId: number
}

// common/types/interfaces.ts

export interface CategoryFieldTemplateInterface {
  id?: number | string
  name: string
  description: string
  type: CategoryFieldType
  deleted?: boolean
  isActive: boolean
  isVisibleForClient: boolean
  isFillByClient: boolean
  required: boolean
  sortOrder: number
  categoryId?: number
  clusterId?: number
  createdAt?: Date
  updatedAt?: Date
}

// common/types/interfaces.ts — добавить

export interface ComplexOrderItemInterface {
  id?: number
  itemId: number
  name: string
  quantity: number
  basePrice: number
  manualPrice?: number | null
  total: number
  complexOrderId?: number
  createdAt?: Date
  updatedAt?: Date
}

export interface ComplexOrderDataInterface {
  id?: number
  complexOrderId?: number
  fieldTemplateId?: number
  label?: string | null
  type: string
  value: string
  createdAt?: Date
  updatedAt?: Date
}

export interface ComplexOrderInterface {
  id?: number
  status: ComplexOrderStatus
  total: number
  phone_number: string
  customerName?: string | null
  desiredTime?: Date | null
  notes?: string | null
  clusterId?: number
  userId?: number | null
  fullAddressId?: number | null
  createdAt?: Date
  updatedAt?: Date
  items?: ComplexOrderItemInterface[]
  data?: ComplexOrderDataInterface[]
}

export interface BillingStatusResponse {
  monthlyPrice: number
  paidUntil: string | null
  blockingDelay: number
  isBlocked: boolean
  daysUntilBlock: number | null
}

// common/types/interfaces.ts

export interface TariffDiscount {
  durationMonths: number
  discountPercent: number
  isActive: boolean
}

export interface TariffRule {
  [key: string]: any
  id?: number
  name: string
  basePrice: number
  specialPrice?: number | null
  allowDiscounts: boolean
  isActive?: boolean
  validFrom?: string | null
  validTo?: string | null
  datesImportant?: boolean
  isPaid?: boolean // ← добавить
  discounts?: TariffDiscount[]
}

export interface MetricsTotal {
  visits: number
  hits: number
  goals: Record<string, number>
}

export interface MetricsSummary {
  domain: string
  today: MetricsTotal
  week: MetricsTotal
  month: MetricsTotal
  quarter: MetricsTotal
}

export interface MetricsPoint {
  date: string
  visits: number
  hits: number
  goals: Record<string, number>
}

export interface MetricsDetailed {
  domain: string
  period: string
  granularity: string
  data: MetricsPoint[]
}

export interface ExecutorClusterResponse {
  cluster: ClusterInterface
  canServe: boolean
  executorId: number
  executorName: string
  position?: string
  isActive: boolean
}

export interface CatUploadInterface {
  id?: number
  fileName: string
  status: CatUploadStatus
  clusterId: number
  categoriesImported?: number
  itemsImported?: number
  imagesImported?: number
  importedAt?: string
  errorMessage?: string
}

export interface CatalogImportDto {
  categories: Array<{
    name: string
    originalLink: string
    description?: string
    categoryAttributes?: any[]
  }>
  items: Array<{
    categoryName: string
    originalLink: string
    name: string
    description?: string
    imageUrls?: string[]
    itemAttributes?: any[]
  }>
  meta: {
    categories: Array<{
      name: string
      cleanBefore?: boolean
    }>
  }
}

export interface DocumentLinkInterface {
  id?: number
  documentId?: number
  clusterId?: number
  entityType: EntityType
  entityId?: number | null
  isAll: boolean
  sortOrder: number
}

export interface DocumentInterface {
  [key: string]: any
  id?: number
  name: string
  description?: string
  filePath?: string
  mimeType?: string
  size?: number
  isPrivate: boolean
  userId?: number
  clusterId?: number
  createdAt?: string
  updatedAt?: string
  links: DocumentLinkInterface[]
  isActive?: boolean
  deleted?: boolean
}

export interface DocumentUploadResultInterface {
  filePath: string
  mimeType: string
  size: number
  originalName: string
}

export interface DocumentPayload {
  name: string
  description?: string
  isPrivate: boolean
  links: string
  file?: FileList
}

export interface TaskInterface {
  id: number
  executorId: number | null
  orderId: number
  clusterId: number
  status: number
  fee: number
  type: number
  createdAt: string
  updatedAt: string
  order?: OrderInterface
  executor?: ExecutorInterface
  cluster?: ClusterInterface
}

export interface TaskListResponse {
  data: TaskInterface[]
  total: number
}

export interface UpdateTaskStatusPayload {
  taskId: number
  status: TaskStatus
  comment?: string
}

export interface ReportTotals {
  count: number
  sum: number
  avg: number
}

export interface ReportByStatus {
  status: number
  count: number
  sum: number
}

export interface ReportByPayment {
  paymentStatus: number
  count: number
  sum: number
}

export interface ReportByDeliveryType {
  deliveryType: number
  count: number
  sum: number
  deliveryFee: number
}

export interface ReportBonuses {
  spent: number
  accrued: number
}

export interface ReportByStore {
  storeId: number
  count: number
  sum: number
}

export interface OrderReportResponse {
  totals: ReportTotals
  byStatus: ReportByStatus[]
  byPayment: ReportByPayment[]
  byDeliveryType: ReportByDeliveryType[]
  bonuses: ReportBonuses
  byStore: ReportByStore[]
}
